import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import { canDownloadClientArtifacts } from '@shared/auth/cognito';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { statusPillClassName } from '@shared/utils/statusPill';
import {
  ApiRecord,
  CONTROL_FILTERS,
  canClientDownloadPackage,
  ControlFilter,
  controlId,
  controlReasonAction,
  controlReasonActionMetadata,
  controlStatus,
  documentControls,
  documentDisplayPath,
  documentId,
  documentReasonAction,
  documentStatus,
  isActionRequired,
  matchesControlFilter,
  packageCounts,
  packageId,
  packageName,
  packageScopeAssetSummary,
  packageScopeExceptionSummary,
  packageScopeReviewMessages,
  hasPackageScopeReviewContext,
  packageScopeSummary,
  packageStage,
  packageStageLabel,
  packageStatus,
  packageType,
  packageTypeLabel,
  packageUpdatedAtDisplay,
  recordFromResponse,
  recordsFromResponse,
  text,
  valueOrDash,
} from './packageUi';
import { openPackageDownload, type PackageDownloadStatus } from './packageDownload';
import { useClientAiAssistant, useClientAiAssistantPageScope } from '../components/ClientAiAssistantDrawer';
import { useClientAccount } from '../account/AccountContext';

type DetailTab = 'overview' | 'controls' | 'documents' | 'actions';

type LoadState = {
  loading: boolean;
  packageRecord: ApiRecord;
  controls: ApiRecord[];
  documents: ApiRecord[];
  error?: string;
};

function detailRow(label: string, value: unknown) {
  return (
    <div className="detail-row package-detail-row" key={label}>
      <span>{label}</span>
      <strong>{valueOrDash(value)}</strong>
    </div>
  );
}

function statusClass(status: string): string {
  return statusPillClassName(status);
}

function buildControlAiPrompt(control: ApiRecord): string {
  const id = controlId(control);
  const title = text(control, 'title', 'name', 'controlTitle');
  const status = controlStatus(control);
  const reason = controlReasonAction(control);
  const parts = [
    `Explain CMMC control ${id || 'this control'}${title ? ` (${title})` : ''} in plain language for a customer user.`,
    'Describe what the control is intended to protect, why it matters for CMMC readiness, what the current status means, what evidence should be reviewed or prepared, and the next client-safe step to move the control toward readiness. Include practical examples of policy, procedure, attestation, operating record, interview, or technical evidence that a customer should review when appropriate.',
  ];
  if (status) parts.push(`Current status: ${status}.`);
  if (reason) parts.push(`Current reason or action: ${reason}.`);
  parts.push('Use the selected package context when available. Keep the answer practical and client-safe. Do not expose internal platform details. Do not perform any portal actions.');
  return parts.join(' ');
}

function isGeneratedMarkdownReportDocument(document: ApiRecord): boolean {
  const candidates = [
    documentId(document),
    text(document, 'documentName'),
    text(document, 'documentKey'),
    text(document, 'path'),
    text(document, 'documentPath'),
    text(document, 'file'),
    text(document, 'preparedDocumentKey'),
    text(document, 'preparedDocumentFileName'),
    text(document, 'uploadedOriginalKey'),
    text(document, 'uploadedOriginalFileName'),
    text(document, 'convertedPdfKey'),
    text(document, 'convertedPdfFileName'),
    documentDisplayPath(document),
  ];

  return candidates.some((value) => {
    const normalized = String(value || '').replace(/\\/g, '/').toUpperCase();
    return Boolean(normalized && (normalized.endsWith('.MD') || normalized.includes('/GENERATED-REPORTS/')));
  });
}


function evidenceWorkspacePackageId(pkg: ApiRecord, fallbackId: string): string {
  const type = packageType(pkg).toUpperCase();
  const stage = packageStage(pkg).toUpperCase();
  const isFinalPackage = type === 'FINAL_AUDITOR_PACKAGE' || stage === 'FINAL_AUDITOR_READY' || canClientDownloadPackage(pkg);
  if (isFinalPackage) {
    return text(
      pkg,
      'sourceTechnicalPackageId',
      'technicalPackageId',
      'sourcePackageId',
      'sourceEvidencePackageId',
      'evidencePackageId',
      'relatedTechnicalPackageId',
    );
  }
  return fallbackId;
}

export default function PackageDetailPage() {
  const { accountPath } = useClientAccount();
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const { packageId: routePackageId } = useParams();
  const decodedPackageId = routePackageId ? decodeURIComponent(routePackageId) : '';
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [controlFilter, setControlFilter] = useState<ControlFilter>('ALL');
  const [documentSearch, setDocumentSearch] = useState('');
  const [state, setState] = useState<LoadState>({ loading: true, packageRecord: {}, controls: [], documents: [] });
  const [downloadStatus, setDownloadStatus] = useState<PackageDownloadStatus>({ state: 'idle' });
  const canDownloadArtifacts = canDownloadClientArtifacts();
  const { openDrawer: openAiAssistant } = useClientAiAssistant();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!decodedPackageId) {
        setState({ loading: false, packageRecord: {}, controls: [], documents: [], error: 'Missing package ID.' });
        return;
      }

      setState((current) => ({ ...current, loading: true, error: undefined }));
      try {
        const [packageResponse, controlsResponse, documentsResponse] = await Promise.all([
          api.get<unknown>(`/client/packages/${encodeURIComponent(decodedPackageId)}`),
          api.get<unknown>(`/client/packages/${encodeURIComponent(decodedPackageId)}/controls`),
          api.get<unknown>(`/client/packages/${encodeURIComponent(decodedPackageId)}/documents`),
        ]);

        if (!cancelled) {
          setState({
            loading: false,
            packageRecord: recordFromResponse(packageResponse, ['package', 'item']),
            controls: recordsFromResponse(controlsResponse, ['controls', 'items']),
            documents: recordsFromResponse(documentsResponse, ['documents', 'items']),
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            loading: false,
            packageRecord: {},
            controls: [],
            documents: [],
            error: error instanceof Error ? error.message : 'Unable to load package details.',
          });
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [api, decodedPackageId]);

  const pkg = state.packageRecord;
  const counts = packageCounts(pkg);
  const filteredControls = state.controls.filter((control) => matchesControlFilter(control, controlFilter));
  const filteredDocuments = state.documents.filter((document) => {
    if (isGeneratedMarkdownReportDocument(document)) return false;
    const query = documentSearch.trim().toUpperCase();
    return !query || JSON.stringify(document).toUpperCase().includes(query);
  });
  const visibleDocuments = state.documents.filter((document) => !isGeneratedMarkdownReportDocument(document));
  const actionControls = state.controls.filter(isActionRequired);
  const actionDocuments = visibleDocuments.filter(isActionRequired);
  const pkgId = packageId(pkg);
  const aiPackageId = decodedPackageId || pkgId;
  const workspacePackageId = evidenceWorkspacePackageId(pkg, aiPackageId);
  const aiPackageType = packageType(pkg) || undefined;
  const aiPackageName = packageName(pkg) || aiPackageId || undefined;
  const aiScope = useMemo(
    () => ({
      packageId: aiPackageId || undefined,
      selectedPackageId: aiPackageId || undefined,
      packageName: aiPackageName,
      packageType: aiPackageType,
      pageType: 'PACKAGE_DETAIL',
      sourceLabel: 'Client package detail page',
      pageContext: {
        pageType: 'PACKAGE_DETAIL',
        packageId: aiPackageId || undefined,
        packageName: aiPackageName,
        packageType: aiPackageType,
        sourceLabel: 'Client package detail page',
      },
    }),
    [aiPackageId, aiPackageName, aiPackageType],
  );
  useClientAiAssistantPageScope(aiScope);
  const canDownloadPackage = canClientDownloadPackage(pkg);
  const isPreparingDownload = downloadStatus.state === 'preparing' && downloadStatus.packageId === pkgId;

  async function handleDownload() {
    if (!canDownloadArtifacts) {
      setDownloadStatus({
        packageId: pkgId,
        state: 'error',
        message: 'Downloads are available only to client administrators. Your current role can still review package status and details.',
      });
      return;
    }
    setDownloadStatus({ packageId: pkgId, state: 'preparing' });
    try {
      await openPackageDownload(api, pkg);
      setDownloadStatus({ state: 'idle' });
    } catch (error) {
      setDownloadStatus({
        packageId: pkgId,
        state: 'error',
        message: error instanceof Error ? error.message : 'Unable to prepare package download.',
      });
    }
  }

  return (
    <div className="page-stack">
      <div className="page-heading page-heading-with-actions">
        <div>
          <h2>Package Detail</h2>
          <p>{valueOrDash(packageName(pkg) || decodedPackageId)}</p>
        </div>
        <Link className="secondary-button" to={accountPath('/evidence-packages')}>Back to packages</Link>
      </div>

      {state.loading && <article className="card"><p>Loading package detail...</p></article>}
      {state.error && <article className="card"><p className="error-text">{state.error}</p></article>}

      {!state.loading && !state.error && (
        <>
          <article className="card dashboard-hero-card package-detail-hero-card">
            <div className="section-title-row">
              <div>
                <h3>{packageName(pkg)}</h3>
                {packageId(pkg) && packageId(pkg) !== packageName(pkg) && (
                  <p className="muted-text">{valueOrDash(packageId(pkg))}</p>
                )}
              </div>
              <span className={statusPillClassName(packageStatus(pkg))}>{packageStatus(pkg)}</span>
            </div>

            <div className="metric-grid readiness-metrics">
              <article className="metric-card metric-success"><span>PASS</span><strong>{counts.pass}</strong></article>
              <article className="metric-card metric-warning"><span>PARTIAL</span><strong>{counts.partial}</strong></article>
              <article className="metric-card metric-error"><span>FAIL</span><strong>{counts.fail}</strong></article>
              <article className="metric-card metric-muted"><span>N/A</span><strong>{counts.notApplicable}</strong></article>
              <article className="metric-card metric-blue"><span>TOTAL</span><strong>{counts.total}</strong></article>
            </div>

            <div className="card-actions">
              {workspacePackageId && (
                <Link
                  className="primary-button"
                  to={accountPath(`/evidence-workspace/${encodeURIComponent(workspacePackageId)}`)}
                >
                  Open Evidence Workspace
                </Link>
              )}
              <button
                className="secondary-button"
                type="button"
                onClick={() => openAiAssistant(
                  aiScope,
                  'What should I work on next for this package?',
                  { autoSubmit: false },
                )}
              >
                Ask AI about this package
              </button>
              {downloadStatus.state === 'error' && downloadStatus.message && (
                <span className="error-text">{downloadStatus.message}</span>
              )}
              {pkgId && canDownloadPackage && canDownloadArtifacts ? (
                <button className="primary-button" type="button" onClick={() => void handleDownload()} disabled={isPreparingDownload}>
                  {isPreparingDownload ? 'Preparing download...' : 'Download auditor package'}
                </button>
              ) : pkgId && canDownloadPackage ? (
                <span className="muted-text">Downloads are admin-only. You can still review the package details on this page.</span>
              ) : (
                <span className="muted-text">Client admins can download final auditor evidence packages only.</span>
              )}
            </div>
          </article>

          <article className="card">
            <div className="tabs" role="tablist" aria-label="Package detail tabs">
              {(['overview', 'controls', 'documents', 'actions'] as DetailTab[]).map((tab) => (
                <button
                  key={tab}
                  className={activeTab === tab ? 'tab-button tab-button-active' : 'tab-button'}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'overview' ? 'Overview' : tab === 'controls' ? `Controls (${state.controls.length})` : tab === 'documents' ? `Documents (${visibleDocuments.length})` : `Actions Required (${actionControls.length + actionDocuments.length})`}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <div className="details-grid package-detail-overview-grid">
                <div className="detail-row package-detail-row"><span>Package type</span><strong><span className={statusPillClassName(packageType(pkg) || packageTypeLabel(pkg))}>{valueOrDash(packageTypeLabel(pkg))}</span></strong></div>
                <div className="detail-row package-detail-row"><span>Package stage</span><strong><span className={statusPillClassName(packageStage(pkg) || packageStageLabel(pkg))}>{valueOrDash(packageStageLabel(pkg))}</span></strong></div>
                <div className="detail-row package-detail-row"><span>Status</span><strong><span className={statusPillClassName(packageStatus(pkg))}>{packageStatus(pkg)}</span></strong></div>
                {detailRow('CMMC level', text(pkg, 'cmmcLevel', 'level'))}
                {detailRow('Job ID', text(pkg, 'jobId'))}
                {detailRow('Source technical package', text(pkg, 'sourceTechnicalPackageId'))}
                {detailRow('Created', formatLocalDateTime(text(pkg, 'createdAt', 'generatedUtc')))}
                {detailRow('Updated', packageUpdatedAtDisplay(pkg))}
                {detailRow('Scope assets', packageScopeSummary(pkg))}
                {detailRow('Scope categories', packageScopeAssetSummary(pkg))}
                {detailRow('Scope exceptions', packageScopeExceptionSummary(pkg))}
                {hasPackageScopeReviewContext(pkg) && (
                  <div className="detail-row package-detail-row package-detail-row-wide">
                    <span>Scope review context</span>
                    <strong>
                      {packageScopeReviewMessages(pkg).map((message) => (
                        <span className="cell-secondary" key={message}>{message}</span>
                      ))}
                    </strong>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'controls' && (
              <div className="tab-panel-stack">
                <div className="filter-grid">
                  <label>
                    Control filter
                    <select value={controlFilter} onChange={(event) => setControlFilter(event.target.value as ControlFilter)}>
                      {CONTROL_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                </div>
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Control</th>
                        <th>Status</th>
                        <th>AI</th>
                        <th>Title</th>
                        <th>Reason / Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredControls.map((control, index) => (
                        <tr key={controlId(control) || index}>
                          <td>{valueOrDash(controlId(control))}</td>
                          <td><span className={statusClass(controlStatus(control))}>{controlStatus(control)}</span></td>
                          <td className="control-ai-cell">
                            <button
                              className="secondary-button compact-action-button ai-row-explain-button"
                              type="button"
                              disabled={!controlId(control)}
                              onClick={() => openAiAssistant(
                                {
                                  ...aiScope,
                                  controlId: controlId(control),
                                  selectedControlId: controlId(control),
                                  controlTitle: text(control, 'title', 'name', 'controlTitle') || undefined,
                                  sourceLabel: 'Package controls table',
                                  pageContext: {
                                    ...aiScope.pageContext,
                                    pageType: 'PACKAGE_DETAIL',
                                    packageId: aiScope.packageId,
                                    packageName: aiScope.packageName,
                                    packageType: aiScope.packageType,
                                    controlId: controlId(control),
                                    controlTitle: text(control, 'title', 'name', 'controlTitle') || undefined,
                                    sourceLabel: 'Package controls table',
                                  },
                                },
                                buildControlAiPrompt(control),
                                {
                                  autoSubmit: true,
                                  displayMessage: `Explain ${controlId(control) || 'this control'} and what evidence is needed.`,
                                },
                              )}
                              title="Ask the SURELINC Readiness AI Assistant™ to explain this control"
                            >
                              Explain
                            </button>
                          </td>
                          <td>{valueOrDash(text(control, 'title', 'name', 'controlTitle'))}</td>
                          <td>
                            <div>{valueOrDash(controlReasonAction(control))}</div>
                            {controlReasonActionMetadata(control) && <small className="muted-text">{controlReasonActionMetadata(control)}</small>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="tab-panel-stack">
                <div className="filter-grid">
                  <label>
                    Search documents
                    <input value={documentSearch} onChange={(event) => setDocumentSearch(event.target.value)} placeholder="Document, status, file, signed, owner" />
                  </label>
                </div>
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Document</th>
                        <th>Status</th>
                        <th>Signed</th>
                        <th>Path</th>
                        <th>Controls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDocuments.map((document, index) => (
                        <tr key={documentId(document) || index}>
                          <td>{valueOrDash(documentId(document))}</td>
                          <td><span className={statusPillClassName(documentStatus(document))}>{documentStatus(document)}</span></td>
                          <td>{valueOrDash(text(document, 'signed', 'isSigned'))}</td>
                          <td>{valueOrDash(documentDisplayPath(document))}</td>
                          <td>{valueOrDash(documentControls(document))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'actions' && (
              <div className="tab-panel-stack">
                <h3>Client Action Required</h3>
                <p className="muted-text">Controls and documents that may require client validation, human/process evidence, scoping confirmation, or final assessor review.</p>
                {actionControls.length + actionDocuments.length === 0 && <p>No client actions are currently indexed for this package.</p>}
                {actionControls.length > 0 && (
                  <>
                    <h4>Controls</h4>
                    <div className="table-scroll">
                      <table className="data-table">
                        <thead><tr><th>Control</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                          {actionControls.map((control, index) => (
                            <tr key={controlId(control) || index}>
                              <td>{valueOrDash(controlId(control))}</td>
                              <td><span className={statusClass(controlStatus(control))}>{controlStatus(control)}</span></td>
                              <td>
                            <div>{valueOrDash(controlReasonAction(control))}</div>
                            {controlReasonActionMetadata(control) && <small className="muted-text">{controlReasonActionMetadata(control)}</small>}
                          </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {actionDocuments.length > 0 && (
                  <>
                    <h4>Documents</h4>
                    <div className="table-scroll">
                      <table className="data-table">
                        <thead><tr><th>Document</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                          {actionDocuments.map((document, index) => (
                            <tr key={documentId(document) || index}>
                              <td>{valueOrDash(documentId(document))}</td>
                              <td><span className={statusPillClassName(documentStatus(document))}>{documentStatus(document)}</span></td>
                              <td>{valueOrDash(documentReasonAction(document))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </article>
        </>
      )}
    </div>
  );
}
